import React, { useState, useEffect, type ComponentType, useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { ExtensionTitle } from '@atlassian/jira-forge-ui-extension-title/src/ui/index.tsx';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import type { ForgeUiIssueData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type {
	CustomField,
	CustomFieldType,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { overrideExtension } from '@atlassian/jira-forge-ui-utils-internal/src/utils/extension/index.tsx';
import { useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useRenderCustomReadView } from './custom-read-view/index.tsx';
import { ForgeCustomFieldEdit } from './edit/index.tsx';
import { useFormatterValue } from './formatter-value/index.tsx';
import type { FieldProps, CustomFieldProps } from './types.tsx';
import { useValueFunction } from './value-function/index.tsx';

type Props = FieldProps<ForgeCustomFieldValue> & {
	shouldUseFormatter: boolean;
	extension: CustomField | CustomFieldType;
	extensionData: ForgeUiIssueData | null;
	fieldName: string;
	ForgeField: ComponentType<CustomFieldProps>;
};

const ForgeCustomFieldViewV2 = ({
	value,
	fieldId,
	fieldName,
	onSave,
	shouldUseFormatter,
	extension,
	extensionData,
	ForgeField,
	...restProps
}: Props) => {
	const issueId = useIssueId();
	const issueKey = useIssueKey();

	const shouldUseValueFunction = useMemo(() => !isNil(extension?.properties.value), [extension]);

	const {
		error: valueFunctionError,
		isLoading: isLoadingValueFunction,
		requestExecuteValueFunction,
	} = useValueFunction(
		issueKey,
		issueId,
		fieldId,
		extension?.properties?.type,
		shouldUseValueFunction,
		onSave,
	);

	const { formatterValue, isLoading: isLoadingFormatter } = useFormatterValue({
		value,
		formatterProperty: extension?.properties.formatter,
		fieldId,
		issueKey,
		issueId,
		shouldUseFormatter,
		isEditing: restProps.isEditing,
	});

	useEffect(() => {
		if (shouldUseValueFunction) {
			requestExecuteValueFunction();
		}
	}, [requestExecuteValueFunction, shouldUseValueFunction]);

	const {
		type,
		collection,
		function: extensionFunction,
		resource: extensionResource,
		render,
	} = extension.properties;

	const shouldRenderCustomReadView =
		!isNil(extensionFunction) ||
		(!isNil(extensionResource) && render === 'native') ||
		(shouldUseFormatter && (formatterValue !== null || isLoadingFormatter)) ||
		(shouldUseValueFunction && (valueFunctionError !== null || isLoadingValueFunction));

	const renderCustomReadView = useRenderCustomReadView({
		extension,
		extensionData,
		type,
		fieldId,
		fieldName,
		collection,
		shouldUseFormatter,
		onUserConsentGranted: undefined, // TODO: remove this prop together with new_graphql_endpoint_for_fetching_forge_modules FG clean up
		formatterValue,
		error: valueFunctionError,
		isLoadingValueFunction,
		onRequestExecuteValueFunction: requestExecuteValueFunction,
		isLoadingFormatter,
	});

	const viewProps: CustomFieldProps = {
		shouldFireScreenEvent: !shouldRenderCustomReadView,
		value,
		fieldId,
		fieldName,
		extension,
		titleView: (
			<ExtensionTitle
				extensionName={restProps.label}
				extensionEnvType={extension.environmentType}
				extensionEnvKey={extension.environmentKey}
			/>
		),
		...(shouldRenderCustomReadView
			? {
					customReadView: renderCustomReadView,
				}
			: null),
		...restProps,
	};

	if (extension.properties.edit) {
		const { onCancel } = restProps;

		return (
			<ForgeCustomFieldEdit
				issueKey={issueKey}
				fieldId={fieldId}
				fieldName={fieldName}
				value={value}
				extension={extension}
				extensionData={extensionData}
				renderCustomReadView={renderCustomReadView}
				shouldRenderCustomReadView={shouldRenderCustomReadView}
				viewProps={viewProps}
				ForgeField={ForgeField}
				onCancel={onCancel}
				onSave={onSave}
			/>
		);
	}

	return <ForgeField {...viewProps} />;
};

const ForgeCustomFieldViewV1 = ({
	value,
	fieldId,
	fieldName,
	onSave,
	shouldUseFormatter,
	extension,
	extensionData,
	ForgeField,
	...restProps
}: Props) => {
	const issueId = useIssueId();
	const issueKey = useIssueKey();

	const shouldUseValueFunction = useMemo(() => !isNil(extension?.properties.value), [extension]);

	const {
		error: valueFunctionError,
		isLoading: isLoadingValueFunction,
		requestExecuteValueFunction,
	} = useValueFunction(
		issueKey,
		issueId,
		fieldId,
		extension?.properties?.type,
		shouldUseValueFunction,
		onSave,
	);

	const { formatterValue, isLoading: isLoadingFormatter } = useFormatterValue({
		value,
		formatterProperty: extension?.properties.formatter,
		fieldId,
		issueKey,
		issueId,
		shouldUseFormatter,
		isEditing: restProps.isEditing,
	});

	useEffect(() => {
		if (shouldUseValueFunction) {
			requestExecuteValueFunction();
		}
	}, [requestExecuteValueFunction, shouldUseValueFunction]);

	// TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-1696 this is a deprecated prop and should be removed
	// @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'undefined'.
	const { requiresUserConsent } = extension || {};
	const [userConsentRequired, setUserConsentRequired] = useState(requiresUserConsent);

	const {
		type,
		collection,
		function: extensionFunction,
		resource: extensionResource,
		render,
	} = extension.properties;

	const shouldRenderCustomReadView =
		!isNil(extensionFunction) ||
		(!isNil(extensionResource) && render === 'native') ||
		(shouldUseFormatter && (formatterValue !== null || isLoadingFormatter)) ||
		(shouldUseValueFunction && (valueFunctionError !== null || isLoadingValueFunction));

	const onUserConsentGranted = useCallback(() => setUserConsentRequired(false), []);
	const renderCustomReadView = useRenderCustomReadView({
		extension,
		extensionData,
		type,
		fieldId,
		fieldName,
		collection,
		shouldUseFormatter,
		onUserConsentGranted,
		formatterValue,
		error: valueFunctionError,
		isLoadingValueFunction,
		onRequestExecuteValueFunction: requestExecuteValueFunction,
		isLoadingFormatter,
	});

	const viewProps: CustomFieldProps = {
		shouldFireScreenEvent: !shouldRenderCustomReadView,
		value,
		fieldId,
		fieldName,
		extension,
		titleView: (
			<ExtensionTitle
				extensionName={restProps.label}
				extensionEnvType={extension.environmentType}
				extensionEnvKey={extension.environmentKey}
			/>
		),
		...(shouldRenderCustomReadView
			? {
					customReadView: renderCustomReadView,
				}
			: null),
		...restProps,
	};

	if (extension.properties.edit) {
		const { onCancel } = restProps;

		return (
			<ForgeCustomFieldEdit
				issueKey={issueKey}
				fieldId={fieldId}
				fieldName={fieldName}
				value={value}
				extension={overrideExtension<CustomFieldType | CustomField>(extension, {
					requiresUserConsent: userConsentRequired,
				})}
				extensionData={extensionData}
				renderCustomReadView={renderCustomReadView}
				shouldRenderCustomReadView={shouldRenderCustomReadView}
				viewProps={viewProps}
				ForgeField={ForgeField}
				onCancel={onCancel}
				onSave={onSave}
			/>
		);
	}

	return <ForgeField {...viewProps} />;
};

export const ForgeCustomFieldView = componentWithCondition(
	() => fg('new_graphql_endpoint_for_fetching_forge_modules'),
	ForgeCustomFieldViewV2,
	ForgeCustomFieldViewV1,
);
